"use client";

import React, { useEffect, useRef, useState } from "react";
import { Shield, Mail, MapPin, CheckCircle2, ArrowRight, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const userRights = [{
  title: "Access & Portability",
  description: "Request a complete digital manifest of your personal data and processing history within our secure infrastructure."
}, {
  title: "Data Precision",
  description: "Ensure your information is accurate by updating any records maintained within our data systems."
}, {
  title: "Erasure Protocols",
  description: "Trigger a permanent wipe of your personal data identifiers across our primary and redundant storage nodes."
}, {
  title: "Processing Limits",
  description: "Temporarily suspend the computational use of your data while maintaining its secure encrypted state."
}, {
  title: "Refusal Rights",
  description: "Object to specific automated processing or decision-making involving your personal profile."
}, {
  title: "Secure Migration",
  description: "Seamlessly transfer your validated data sets to other certified providers via our high-speed API."
}];
const contactMethods = [{
  icon: Mail,
  title: "Direct DPO Uplink",
  detail: "dpo@datacenter.ai",
  description: "For technical privacy inquiries and formal data subject requests."
}, {
  icon: MapPin,
  title: "Security Operations",
  detail: "Data Governance Division",
  description: "500 AI Plaza, Suite 900, Tech Hub, CA 94043"
}];
export default function UserRightsContactInformation() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="165217" id="user-rights-and-data-protection" className="bg-background text-foreground font-sans py-16 md:py-24 lg:py-32 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        <div ref={sectionRef} className={`grid gap-12 lg:grid-cols-2 lg:gap-16 items-start transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {}
          <div className="space-y-8">
            <div className="space-y-4">
              <div className="inline-flex items-center rounded-full border border-violet-500/20 bg-violet-500/10 px-3 py-1 text-sm font-medium text-violet-600 dark:text-violet-400">
                <Shield className="mr-2 h-4 w-4" />
                Enterprise-Grade Security
              </div>
              <h2 className="text-4xl font-bold tracking-tighter sm:text-5xl md:text-6xl text-foreground">
                Your Data Rights & Privacy Governance
              </h2>
              <p className="text-xl text-muted-foreground max-w-[600px] leading-relaxed">
                We ensure your data remains under your absolute control with enterprise-grade protection protocols within our secure infrastructure.
              </p>
            </div>

            <div className="grid gap-6 sm:grid-cols-2">
              {userRights.map((right, i) => <div key={i} data-index={i} className="flex flex-col gap-2 p-4 rounded-xl bg-card/30 backdrop-blur-sm border border-border/40 hover:border-violet-500/30 transition-colors">
                  <div className="flex items-center gap-2 text-foreground font-semibold">
                    <CheckCircle2 className="h-5 w-5 text-violet-500 shrink-0" />
                    <h3 className="leading-tight text-lg">{right.title}</h3>
                  </div>
                  <p className="text-sm text-muted-foreground pl-7 leading-snug">
                    {right.description}
                  </p>
                </div>)}
            </div>

            <div className="pt-8 border-t border-border/50">
              <p className="text-sm text-muted-foreground mb-6">
                For a technical deep-dive into our encryption standards and data handling ethics, please review our core documentation.
              </p>
              <Button variant="outline" className="rounded-full border-primary/20 hover:bg-primary/5" asChild>
                <Link to="/SupportPage">
                  <FileText className="mr-2 h-4 w-4" />
                  Read Data Governance Policy
                </Link>
              </Button>
            </div>
          </div>

          {}
          <div className="lg:pl-8">
            <Card className="border-border/40 shadow-2xl bg-card/40 backdrop-blur-xl rounded-2xl overflow-hidden">
              <CardHeader className="space-y-3 p-8">
                <CardTitle className="text-3xl font-bold tracking-tight">Data Protection Officer</CardTitle>
                <CardDescription className="text-lg leading-relaxed">
                  Our dedicated DPO ensures all data handling adheres to global compliance standards. Reach out for technical inquiries or data requests.
                </CardDescription>
              </CardHeader>
              <CardContent className="grid gap-6 px-8 py-4">
                {contactMethods.map((method, i) => <div key={i} data-index={i} className="flex items-start gap-5 p-5 rounded-xl bg-background/40 border-b-2 border-transparent hover:border-violet-500/30 transition-all">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-violet-500/10 text-violet-500">
                      <method.icon className="h-6 w-6" />
                    </div>
                    <div className="space-y-1">
                      <h4 className="font-semibold text-lg leading-none text-foreground">
                        {method.title}
                      </h4>
                      <p className="text-sm font-mono text-primary py-1">
                        {method.detail}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {method.description}
                      </p>
                    </div>
                  </div>)}
              </CardContent>
              <CardFooter className="flex flex-col items-start gap-4 p-8 bg-muted/20">
                <div className="bg-background/60 backdrop-blur-md rounded-xl p-6 w-full border border-border/40">
                  <h4 className="font-bold text-foreground mb-2 text-base tracking-tight uppercase text-xs">Initiate Protocol</h4>
                  <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
                    To exercise your rights (such as requesting access to or deletion of your data), please use our secure request portal. We process all legitimate requests within 30 days.
                  </p>
                  <Button variant="ghost" className="w-full sm:w-auto group bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg" asChild>
                    <Link to="/ContactPage">
                      Initiate Data Protocol
                      <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </Button>
                </div>
              </CardFooter>
            </Card>
          </div>
        </div>
      </div>
    </section>;
}